import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { notifyPhotographerPaymentConfirmed, sendNotification } from '../../services/notificationService';
import { bookingService } from '../../services/bookingService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const BookingPaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Moyasar يُرجع: ?id=PAYMENT_ID  (نحن أضفنا booking_id في callback_url)
  const moyasarPaymentId = searchParams.get('id');
  const bookingId = searchParams.get('booking_id');

  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'failed'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (moyasarPaymentId && bookingId) {
      verifyAndConfirm();
    } else {
      setStatus('failed');
      setErrorMessage('معلومات الدفع غير مكتملة');
    }
  }, [moyasarPaymentId, bookingId]);

  const verifyAndConfirm = async () => {
    try {
      setStatus('processing');

      // ── 1. التحقق من الدفع عبر Edge Function (الـ Secret Key على الخادم فقط) ──
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'verify-moyasar-payment',
        {
          body: { payment_id: moyasarPaymentId, booking_id: bookingId },
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }
      );

      if (fnError) {
        // استخراج الرسالة الحقيقية من جسم الاستجابة
        let realErrorMessage = fnError.message;
        try {
          const ctx = fnError.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) realErrorMessage = body.error;
          }
        } catch { /* ignore parse error */ }
        throw new Error(realErrorMessage);
      }

      if (!fnData?.success) {
        throw new Error(fnData?.error || 'فشل التحقق من الدفع');
      }

      // ── 2. إشعارات (غير مُوقِفة للتدفق) ────────────────────────────────────────
      try {
        const { triggerPushNotification } = await import('../../pwa/triggerPush');
        triggerPushNotification(bookingId, 'PAYMENT_CONFIRMED');
      } catch (e) {
        console.warn('PWA push failed (non-blocking):', e);
      }

      try {
        const booking = await bookingService.getBookingById(bookingId);

        // WhatsApp للمصور
        if (booking?.photographerId || booking?.photographer_id) {
          const { whatsappService } = await import('../../services/whatsappService');
          await whatsappService.notifyPhotographerBookingPaid(
            booking.photographerId || booking.photographer_id,
            bookingId
          ).catch((e) => console.warn('WhatsApp failed:', e));
        }

        // إشعار داخل التطبيق للعميل
        const customerId = booking?.customerId || booking?.customer_id;
        if (customerId) {
          await sendNotification({
            userId: customerId,
            type: 'payment',
            title: 'تم تأكيد الدفع ✅',
            message: 'تم تأكيد حجزك ودفعه بنجاح! سيتواصل معك المصور قريباً.',
            bookingId,
            fcmData: { type: 'PAYMENT_CONFIRMED' },
          }).catch((e) => console.warn('Customer notification failed:', e));
        }

        // إشعار داخل التطبيق للمصور
        const photographerId = booking?.photographerId || booking?.photographer_id;
        if (photographerId) {
          await notifyPhotographerPaymentConfirmed(photographerId, bookingId)
            .catch((e) => console.warn('Photographer notification failed:', e));
        }
      } catch (notifError) {
        console.warn('Notifications failed (non-blocking):', notifError);
      }

      // GA4: حدث إتمام دفع ناجح — يُستخدم كإحالة ناجحة في Google Ads
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'purchase_completed', {
          event_category: 'payment',
          event_label: 'booking_payment',
          transaction_id: bookingId,
          value: 1,
        });
      }

      setStatus('success');

      // انتقال تلقائي لصفحة تأكيد الحجز بعد 3 ثوانٍ
      setTimeout(() => {
        navigate(`/booking-confirmation?id=${bookingId}`, { replace: true });
      }, 3000);

    } catch (error) {
      console.error('Payment verification failed:', error);
      setErrorMessage(error.message || 'حدث خطأ أثناء التحقق من الدفع');
      setStatus('failed');
    }
  };

  // ── Processing ───────────────────────────────────────────────────────────────
  if (status === 'processing') {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center"
        dir="rtl"
      >
        <div className="text-center max-w-sm mx-auto px-4">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">جاري التحقق من الدفع</h2>
          <p className="text-slate-500 text-sm">يرجى الانتظار، لا تغلق هذه الصفحة...</p>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-green-50 via-slate-50 to-slate-50 flex items-center justify-center"
        dir="rtl"
      >
        <div className="max-w-lg mx-auto px-4 w-full">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-12 text-center">

            {/* أيقونة نجاح متحركة */}
            <div className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-once">
              <Icon name="CheckCircle" size={56} className="text-success" />
            </div>

            <h1 className="text-3xl font-bold text-slate-900 mb-3">تم الدفع بنجاح!</h1>
            <p className="text-slate-600 mb-2">تم تأكيد حجزك ودفعه بنجاح.</p>
            <p className="text-slate-500 text-sm mb-8">سيتواصل معك المصور قريبًا لتنسيق التفاصيل.</p>

            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 text-sm text-green-700">
              سيتم تحويلك تلقائيًا لتفاصيل الحجز خلال ثوانٍ...
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="default"
                onClick={() => navigate(`/booking-confirmation?id=${bookingId}`, { replace: true })}
                iconName="FileText"
                iconPosition="right"
              >
                عرض تفاصيل الحجز
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/customer-dashboard/bookings', { replace: true })}
                iconName="Calendar"
              >
                جميع الحجوزات
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Failed ───────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-red-50 via-slate-50 to-slate-50 flex items-center justify-center"
      dir="rtl"
    >
      <div className="max-w-lg mx-auto px-4 w-full">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-12 text-center">

          <div className="w-24 h-24 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="XCircle" size={56} className="text-error" />
          </div>

          <h1 className="text-3xl font-bold text-slate-900 mb-3">لم يتم الدفع</h1>
          <p className="text-slate-600 mb-2">لم يتم تأكيد الدفع بنجاح.</p>
          {errorMessage && (
            <p className="text-sm text-slate-400 mb-8 bg-slate-50 rounded-lg p-3">{errorMessage}</p>
          )}
          {!errorMessage && <div className="mb-8" />}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {bookingId && (
              <Button
                variant="default"
                onClick={() => navigate(`/booking-payment/${bookingId}`, { replace: true })}
                iconName="CreditCard"
                iconPosition="right"
              >
                إعادة المحاولة
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/customer-dashboard/bookings', { replace: true })}
              iconName="ArrowRight"
            >
              العودة إلى الحجوزات
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPaymentCallback;
